import { useEffect, useRef, useState } from 'react';
import type { PhotoList, PublicPhoto, PublicState } from '@karozik/shared';
import { api } from '@karozik/ui';

export interface WallSlide {
  photo: PublicPhoto;
  /** Shared since the wall started: the screen says so. */
  fresh: boolean;
  /** Increases with every slide, for keys and animations. */
  n: number;
}

/** Resolves once the photo is decoded, so it never shows up half loaded. */
async function ready(url: string): Promise<boolean> {
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
    return true;
  } catch {
    return false;
  }
}

function shuffle<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/**
 * Decides which shared photo the big screen shows, and when: photos just
 * shared first (with `newFirst`), then the whole library in a shuffled loop.
 * Null when the screen shows its usual self: wall off, a game, a moment or the
 * mic on, no photo yet, or between two photos in interlude mode.
 */
export function usePhotoWall(state: PublicState | null): WallSlide | null {
  const wall = state?.staff?.photoWall;
  const eventId = state?.event.id ?? '';
  const enabled = !!wall?.enabled;
  const busy = !state || state.event.status !== 'live' || !!state.blindtest || !!state.faces || !!state.moment || state.mic.active;
  const on = enabled && !busy;
  const mode = wall?.mode ?? 'interlude';

  // Durations are read when the next photo is scheduled: moving a slider on the
  // remote doesn't restart the wall.
  const config = useRef(wall);
  config.current = wall;

  const photos = useRef(new Map<string, PublicPhoto>());
  const known = useRef<Set<string> | null>(null);
  const fresh = useRef<string[]>([]);
  const deck = useRef<string[]>([]);
  const last = useRef<string | null>(null);
  const count = useRef(0);
  const wake = useRef<(() => void) | null>(null);
  const [slide, setSlide] = useState<WallSlide | null>(null);

  // The library, reloaded whenever a photo is added or removed.
  useEffect(() => {
    if (!enabled || !eventId) {
      known.current = null;
      fresh.current = [];
      return;
    }
    let alive = true;
    api<PhotoList>(`/api/admin/events/${eventId}/photos`)
      .then((list) => {
        if (!alive) return;
        photos.current = new Map(list.photos.map((p) => [p.id, p]));
        // The first load is the library as it stands; after that, unseen ids were just shared.
        const seen = known.current;
        if (seen) fresh.current.push(...list.photos.filter((p) => !seen.has(p.id)).reverse().map((p) => p.id));
        known.current = new Set(list.photos.map((p) => p.id));
        wake.current?.();
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [enabled, eventId, state?.photoCount]);

  useEffect(() => {
    if (!on) {
      setSlide(null);
      return;
    }
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let waiting: 'photos' | 'gap' | null = null;
    let showing: string | null = null;
    const later = (fn: () => void, ms: number) => {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };
    const newFirst = () => config.current?.newFirst ?? true;

    const pick = (): Omit<WallSlide, 'n'> | null => {
      const all = photos.current;
      if (newFirst()) {
        while (fresh.current.length > 0) {
          const p = all.get(fresh.current.shift()!);
          if (p) return { photo: p, fresh: true };
        }
      } else fresh.current = [];
      for (let lap = 0; lap < 2; lap++) {
        while (deck.current.length > 0) {
          const p = all.get(deck.current.pop()!);
          if (p) return { photo: p, fresh: false };
        }
        // A new lap, never opening on the photo just shown.
        deck.current = shuffle([...all.keys()].filter((id) => id !== last.current || all.size === 1));
      }
      return null;
    };

    const show = async () => {
      waiting = null;
      const next = pick();
      if (!next) {
        waiting = 'photos';
        showing = null;
        setSlide(null);
        return;
      }
      const ok = await ready(next.photo.url);
      if (!alive) return;
      if (!ok) return later(() => void show(), 1000);
      last.current = showing = next.photo.id;
      setSlide({ ...next, n: ++count.current });
      later(after, (config.current?.seconds ?? 8) * 1000);
    };

    const after = () => {
      if (mode === 'continuous' || (newFirst() && fresh.current.length > 0)) return void show();
      setSlide(null);
      showing = null;
      waiting = 'gap';
      later(() => void show(), (config.current?.gapSeconds ?? 45) * 1000);
    };

    wake.current = () => {
      // Deleted from the remote while on screen: off it goes, right away.
      if (showing && !photos.current.has(showing)) {
        clearTimeout(timer);
        return after();
      }
      if (waiting === 'photos' || (waiting === 'gap' && newFirst() && fresh.current.length > 0)) {
        clearTimeout(timer);
        void show();
      }
    };
    void show();
    return () => {
      alive = false;
      clearTimeout(timer);
      wake.current = null;
    };
  }, [on, mode]);

  return slide;
}
